/**
 * Single source of truth for the site's canonical origin.
 *
 * Every SEO surface (metadata, OG, canonical, sitemap, robots, JSON-LD)
 * derives its absolute URLs from here. We deliberately do NOT provide a
 * production fallback: if `NEXT_PUBLIC_SITE_URL` is missing in an
 * environment that calls into this module, the resolver throws so the
 * error is loud at build/render time rather than producing inconsistent
 * cross-host links.
 */

export const SITE_NAME = "Binarly";

/**
 * Default English locale used for OpenGraph / metadata. Update if/when
 * we introduce other locales.
 */
export const SITE_LOCALE = "en_US";

/** Production apex host; Vercel redirects this to `www`. */
const APEX_HOST = "binarly.io";
/** Canonical host for all emitted SEO / OG / sitemap URLs. */
const CANONICAL_HOST = "www.binarly.io";

let memoizedSiteUrl: string | null = null;

/**
 * Map apex `binarly.io` to `www.binarly.io` so canonical, OG, and share
 * URLs match where Slack and other crawlers actually fetch the page.
 */
function normalizeCanonicalHost(url: URL): URL {
  if (url.hostname === APEX_HOST) {
    url.hostname = CANONICAL_HOST;
  }
  return url;
}

/**
 * Returns the canonical origin (no trailing slash) for the current site,
 * derived from `NEXT_PUBLIC_SITE_URL`. Throws if the env var is unset or
 * invalid so misconfigured environments fail loud instead of silently
 * emitting broken links.
 */
export function getSiteUrl(): string {
  if (memoizedSiteUrl) return memoizedSiteUrl;

  const raw = process.env.NEXT_PUBLIC_SITE_URL;
  if (!raw || raw.trim() === "") {
    throw new Error(
      "[seo/site] NEXT_PUBLIC_SITE_URL is not set. Every canonical/OG/sitemap URL " +
        "derives from this value; configure it in your environment before running."
    );
  }

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    throw new Error(
      `[seo/site] NEXT_PUBLIC_SITE_URL is not a valid URL: "${raw}".`
    );
  }

  normalizeCanonicalHost(parsed);
  memoizedSiteUrl = `${parsed.protocol}//${parsed.host}`.replace(/\/$/, "");
  return memoizedSiteUrl;
}

/**
 * Convert a path (or already-absolute URL) to an absolute URL anchored
 * at the canonical site origin. Idempotent on absolute URLs.
 */
export function absoluteUrl(pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return getSiteUrl();
  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      return normalizeCanonicalHost(new URL(pathOrUrl)).toString();
    } catch {
      return pathOrUrl;
    }
  }

  const normalized = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${getSiteUrl()}${normalized}`;
}

/**
 * Return a URL object pointing at the canonical origin. Useful for
 * Next.js `metadataBase`.
 */
export function getMetadataBase(): URL {
  return new URL(getSiteUrl());
}
